/**
 * RBAC helper utilities for frontend
 *
 * Provides utilities for checking permissions and improving UX
 */

import { isPermissionDeniedError } from './apiErrorHandler'
import { readAssumedRoleName } from './assumedRoleHeader'
import { canManageProjectWork } from './projectWorkAccess'

/** Shown when agent-workflows edit is missing or the user is a project viewer. */
export const WORKFLOW_CONFIGURATION_VIEW_ONLY_TITLE =
  "You don't have permission to edit this workflow's configuration"

/** Shown when the user has edit access but is not the workflow creator (or admin). */
export const WORKFLOW_METADATA_VIEW_ONLY_TITLE =
  'Only the workflow creator or an administrator can edit workflow metadata'

/**
 * Check if current user can edit/delete an asset
 *
 * @param assetCreatedBy - The user ID who created the asset
 * @param currentUserId - Current user's ID
 * @param currentUser - Current user object (optional, to check admin status)
 * @returns True if user can edit/delete (is owner or admin)
 *
 * Note: Admin check should be done server-side, but this helps with UI indicators
 */
export function canUserModifyAsset(
  assetCreatedBy: string | null | undefined,
  currentUserId: string | null,
  currentUser?: { roles?: Array<{ name: string }> } | null
): boolean {
  if (!currentUserId) {
    return false
  }

  // Admins can modify any asset. A Switch Role preview follows the assumed role.
  if (currentUser && isEffectiveAdmin(currentUser)) {
    return true
  }

  // Users can modify assets they created
  if (!assetCreatedBy) {
    return false
  }

  return assetCreatedBy === currentUserId
}

export interface WorkflowCreatorUser {
  id: string
  email?: string | null
  roles?: Array<{ name: string }>
  is_platform_admin?: boolean
}

/** True when ``createdBy`` matches the signed-in user's id or email. */
export function isWorkflowCreator(
  createdBy: string | null | undefined,
  currentUser: WorkflowCreatorUser | null | undefined,
): boolean {
  if (!createdBy || !currentUser) {
    return false
  }
  const email = currentUser.email?.trim()
  return createdBy === currentUser.id || (email ? createdBy === email : false)
}

/**
 * Canvas / node configuration: agent-workflows edit plus project manage-work
 * (viewers stay read-only).
 */
export function canEditWorkflowConfiguration(
  hasAgentWorkflowsEdit: boolean,
  projectId: string | null | undefined,
  accessByProjectId: ReadonlyMap<string, boolean>,
  catalogLoaded: boolean,
): boolean {
  if (!hasAgentWorkflowsEdit) {
    return false
  }
  return canManageProjectWork(projectId, accessByProjectId, catalogLoaded)
}

/**
 * Evaluate / View Evaluation Runs: evals capability plus project manage-work.
 * Virtual / unscoped assets (no project_id) stay on the capability check alone.
 */
export function canManageProjectEvals(
  hasEvalsCapability: boolean,
  projectId: string | null | undefined,
  accessByProjectId: ReadonlyMap<string, boolean>,
  catalogLoaded: boolean,
): boolean {
  if (!hasEvalsCapability) {
    return false
  }
  if (!projectId) {
    return true
  }
  return canManageProjectWork(projectId, accessByProjectId, catalogLoaded)
}

/**
 * Workflow metadata (name, description, tags, icon, catalog fields): edit grant
 * plus creator or tenant/platform admin.
 */
export function canEditWorkflowMetadata(
  hasAgentWorkflowsEdit: boolean,
  workflowCreatedBy: string | null | undefined,
  currentUser: WorkflowCreatorUser | null | undefined,
  isPlatformAdmin = false,
): boolean {
  if (!hasAgentWorkflowsEdit || !currentUser) {
    return false
  }
  if (isEffectiveAdmin(currentUser, isPlatformAdmin)) {
    return true
  }
  return isWorkflowCreator(workflowCreatedBy, currentUser)
}

/**
 * Get empty state message based on context
 *
 * @param resourceName - Name of the resource (e.g., "models", "prompts")
 * @param hasError - Whether there was an error loading
 * @param error - The error object if any
 * @param isSearching - Whether user is searching/filtering
 * @returns Appropriate empty state message
 */
export interface EmptyStateMessageOverrides {
  /** Translated text for the permission-denied case; falls back to the English default when omitted. */
  permissionDenied?: string
  /** Translated text for the "no results match your search" case; falls back to the English default when omitted. */
  noResultsSearch?: string
  /** Translated text for the default (no error, not searching) case; falls back to the English default when omitted. */
  default?: string
}

export function getEmptyStateMessage(
  resourceName: string,
  hasError: boolean = false,
  error: unknown = null,
  isSearching: boolean = false,
  overrides?: EmptyStateMessageOverrides
): string {
  // If there's a permission error, show access-related message
  if (hasError && error && isPermissionDeniedError(error)) {
    return overrides?.permissionDenied ?? `You don't have permission to view ${resourceName}. Contact your administrator for access.`
  }

  // If searching, show search-specific message
  if (isSearching) {
    return overrides?.noResultsSearch ?? `No ${resourceName} found matching your search.`
  }

  // Default empty state
  return overrides?.default ?? `No ${resourceName} found. Create your first ${resourceName.slice(0, -1)} to get started.`
}

/**
 * Get resource name from error context
 *
 * @param endpoint - API endpoint that failed
 * @returns Resource name (e.g., "model", "prompt", "tool")
 */
export function getResourceNameFromEndpoint(endpoint: string): string {
  const resourceMap: Record<string, string> = {
    '/models': 'model',
    '/prompts': 'prompt',
    '/tools': 'tool',
    '/knowledge': 'knowledge connector',
    '/connections': 'connection',
    '/agents': 'agent',
    '/projects': 'project',
  }

  for (const [path, resource] of Object.entries(resourceMap)) {
    if (endpoint.includes(path)) {
      return resource
    }
  }

  return 'resource'
}

/**
 * Get current user ID from auth context
 * Note: This should be called from within a component that has access to AuthContext
 * For use in utilities, pass currentUserId as a parameter instead
 */
export function getCurrentUserId(currentUser: { id: string } | null): string | null {
  return currentUser?.id || null
}

/**
 * Check if user has admin role (Admin or Platform Admin)
 *
 * @param currentUser - Current user object with roles array
 * @returns True if user has Admin or Platform Admin role
 */
function normalizeRoleName(name: string): string {
  return name.toLowerCase().replace(/[\s-]/g, '_')
}

/**
 * Admin for UI breadth. While a Switch Role preview is stored, true only when
 * the assumed role is tenant admin. Otherwise the caller's real admin status.
 */
export function isEffectiveAdmin(
  currentUser: { roles?: Array<{ name: string }>; is_platform_admin?: boolean } | null,
  isPlatformAdmin = false,
): boolean {
  const assumedName = readAssumedRoleName()
  if (assumedName !== null) {
    return normalizeRoleName(assumedName) === 'admin'
  }
  return isUserAdmin(currentUser) || isPlatformAdmin
}

/** Platform-admin UI (all-tenants) stays off for the whole preview. */
export function isEffectivePlatformAdmin(isPlatformAdmin: boolean): boolean {
  return readAssumedRoleName() === null && isPlatformAdmin
}

/** Fields that change what an open tab is allowed to load. */
export interface RoleAccessSnapshot {
  is_platform_admin?: boolean
  roles?: Array<{
    id?: string
    name?: string
    permissions?: unknown
    attached_policies?: unknown
  }>
}

/**
 * Stable fingerprint of platform-admin and every role's grants.
 * Order of roles does not matter. Any edit, including a non-admin policy
 * change, produces a new value so cached lists can be dropped together.
 */
export function roleAccessSignature(user: RoleAccessSnapshot | null): string {
  if (!user) return ''
  const roles = [...(user.roles ?? [])]
    .map((role) => ({
      id: role.id ?? '',
      name: role.name ?? '',
      permissions: role.permissions ?? null,
      attached_policies: role.attached_policies ?? null,
    }))
    .sort((a, b) => a.id.localeCompare(b.id) || a.name.localeCompare(b.name))
  return JSON.stringify({
    is_platform_admin: Boolean(user.is_platform_admin),
    roles,
  })
}

export function isUserAdmin(
  currentUser: { roles?: Array<{ name: string }>; is_platform_admin?: boolean } | null,
): boolean {
  if (!currentUser) {
    return false
  }

  if (currentUser.is_platform_admin) {
    return true
  }

  if (!currentUser.roles || currentUser.roles.length === 0) {
    return false
  }
  // Check if any role has name "Admin" or "Platform Admin" (case-insensitive)
  const adminRoles = new Set(['admin', 'platform admin', 'platform_admin'])
  return currentUser.roles.some((role) =>
    adminRoles.has((role.name ?? '').toLowerCase()),
  )
}

/**
 * Check if error is likely due to RBAC filtering (empty result vs permission denied)
 *
 * @param error - Error object
 * @param totalItems - Total items count from API
 * @returns True if likely RBAC-related empty result
 */
export function isLikelyRbacFiltered(error: unknown, totalItems: number): boolean {
  // If no error but totalItems is 0, might be RBAC filtered
  // This is a heuristic - we can't be 100% sure without backend metadata
  return !error && totalItems === 0
}
